import React, { useState, useEffect } from 'react';
import { Alert, Button } from 'antd';
import { ExclamationCircleOutlined, AlertOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useLicenseStatus } from '../hooks/useLicenseStatus';

const DISMISS_KEY = 'license_banner_dismissed_at';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 часа

const isDismissedForToday = (): boolean => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  return Date.now() - ts < DISMISS_TTL_MS;
};

const LicenseWarningBanner: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => isDismissedForToday());

  // Сбрасываем закрытие раз в сутки (проверяем при монтировании)
  useEffect(() => {
    if (!isDismissedForToday()) {
      setDismissed(false);
    }
  }, []);

  const { data: userData } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await api.get('/auth/me');
      return response.data.data;
    },
  });

  const { data: licenseStatus, isLoading } = useLicenseStatus();

  if (dismissed || isLoading || !userData?.is_admin || !licenseStatus) {
    return null;
  }

  const { mode, expires_at, grace_expires_at, heartbeat_status, last_heartbeat_at } = licenseStatus;

  const getDaysRemaining = (dateString?: string): number | null => {
    if (!dateString) return null;
    const diffTime = new Date(dateString).getTime() - Date.now();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getHoursSince = (dateString?: string): number | null => {
    if (!dateString) return null;
    const diffTime = Date.now() - new Date(dateString).getTime();
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    return diffHours > 0 ? diffHours : 0;
  };

  const getBannerConfig = () => {
    const graceDaysRemaining = getDaysRemaining(grace_expires_at);
    const licenseDaysRemaining = getDaysRemaining(expires_at);
    const hoursSinceHeartbeat = getHoursSince(last_heartbeat_at);

    if (mode === 'none') {
      return {
        type: 'error' as const,
        icon: <ExclamationCircleOutlined />,
        message: t('licenseWarningBanner.notActiveMessage'),
        description: t('licenseWarningBanner.notActiveDescription'),
        showButton: true,
        buttonText: t('licenseWarningBanner.activateButton'),
      };
    }

    if (mode === 'expired') {
      return {
        type: 'error' as const,
        icon: <ExclamationCircleOutlined />,
        message: t('licenseWarningBanner.expiredMessage'),
        description: t('licenseWarningBanner.expiredDescription', { date: expires_at ? new Date(expires_at).toLocaleDateString() : t('licenseWarningBanner.dateUnknown') }),
        showButton: true,
        buttonText: t('licenseWarningBanner.activateButton'),
      };
    }

    if (mode === 'grace') {
      const days = graceDaysRemaining ?? 0;
      if (days <= 7) {
        return {
          type: 'warning' as const,
          icon: <AlertOutlined />,
          message: t('licenseWarningBanner.graceMessage', { count: days }),
          description: t('licenseWarningBanner.graceDescription'),
          showButton: true,
          buttonText: t('licenseWarningBanner.activateButton'),
        };
      }
    }

    if (heartbeat_status === 'revoked') {
      return {
        type: 'error' as const,
        icon: <ExclamationCircleOutlined />,
        message: t('licenseWarningBanner.revokedMessage'),
        description: t('licenseWarningBanner.revokedDescription'),
        showButton: false,
        buttonText: '',
      };
    }

    if (heartbeat_status === 'offline' && hoursSinceHeartbeat && hoursSinceHeartbeat > 24) {
      return {
        type: 'warning' as const,
        icon: <AlertOutlined />,
        message: t('licenseWarningBanner.offlineMessage'),
        description: t('licenseWarningBanner.offlineDescription', { count: hoursSinceHeartbeat }),
        showButton: false,
        buttonText: '',
      };
    }

    if (mode === 'licensed' && licenseDaysRemaining !== null && licenseDaysRemaining <= 7) {
      const days = licenseDaysRemaining;
      return {
        type: 'warning' as const,
        icon: <AlertOutlined />,
        message: t('licenseWarningBanner.expiringMessage', { count: days }),
        description: t('licenseWarningBanner.expiringDescription', { date: expires_at ? new Date(expires_at).toLocaleDateString() : t('licenseWarningBanner.dateUnknown') }),
        showButton: true,
        buttonText: t('licenseWarningBanner.renewButton'),
      };
    }

    return null;
  };

  const bannerConfig = getBannerConfig();

  if (!bannerConfig) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const handleButtonClick = () => {
    navigate('/settings/license');
  };

  return (
    <Alert
      message={bannerConfig.message}
      description={
        <div>
          <div>{bannerConfig.description}</div>
          {bannerConfig.showButton && (
            <Button
              type="primary"
              size="small"
              onClick={handleButtonClick}
              style={{ marginTop: 8 }}
            >
              {bannerConfig.buttonText}
            </Button>
          )}
        </div>
      }
      type={bannerConfig.type}
      showIcon
      icon={bannerConfig.icon}
      closable
      onClose={handleDismiss}
      style={{
        margin: '16px 24px',
        borderRadius: '8px',
      }}
    />
  );
};

export default LicenseWarningBanner;
